import type { FlowRates } from "@/libs/calculations/productionRates";
import type {
  BasicData,
  FluidPropertiesOverrides,
  FluidPropertiesContext
} from "@/libs/calculations/fluidProperties/d";
import type { Correlations } from "../forms/formTypes";
import { getFluidPropertiesContext } from "@/libs/calculations/fluidProperties/getFluidPropertiesContext";
import type { LagrangeInterpolationMapWithGroupings } from "./lagrangeInterpolation";

export type FluidPropertiesCalculationContexts = {
  hpsFluidPropertiesAtIntakeContext: FluidPropertiesContext;
  hpsFluidPropertiesAtPumpIntakeContext: FluidPropertiesContext;
  hpsFluidPropertiesAfterPumpDischargeContext: FluidPropertiesContext;
  hpsFluidPropertiesAfterAllGasSeparationAtPumpContext: FluidPropertiesContext;
};

export type HPSFluidPropertiesPipelineResults = {
  fluidPropertiesCalculationContexts: FluidPropertiesCalculationContexts;
};

export const performHPSFluidPropertiesCalculations = (
  // well properties
  basicData: BasicData,
  correlations: Correlations,
  desiredFlowRates: FlowRates,
  requiredDischargePressurePSIA: number,
  bubblePointPressurePSIA: number,
  condensateApi: number | null,
  dewPointPressurePSIA: number,
  customFluidProperties: LagrangeInterpolationMapWithGroupings | null,
  customViscosityProperties: LagrangeInterpolationMapWithGroupings | null,
  fluidPropertiesOverrides: FluidPropertiesOverrides,
  // intake
  primaryIntakeInnerDiameterInches: number,
  // pump
  primaryPumpInnerDiameterInches: number
  // Overrides needed for perf evals with hps eventually?
  // overrides?: Partial<ScenarioCalculations>
): HPSFluidPropertiesPipelineResults => {
  // When HPS, bottomTubingPressurePSIA is the hps inlet pressure
  const hpsBasicData = {
    ...basicData,
    staticBottomholePressurePSIA: basicData.bottomTubingPressurePSIA
  };
  const hpsFluidPropertiesAtIntakeContext = getFluidPropertiesContext(
    hpsBasicData,
    correlations,
    hpsBasicData.staticBottomholePressurePSIA,
    hpsBasicData.bottomholeTemperatureF,
    primaryIntakeInnerDiameterInches,
    desiredFlowRates,
    undefined,
    undefined,
    customFluidProperties,
    customViscosityProperties,
    bubblePointPressurePSIA,
    dewPointPressurePSIA,
    0,
    0,
    fluidPropertiesOverrides,
    condensateApi
  );

  const hpsFluidPropertiesAtPumpIntakeContext = getFluidPropertiesContext(
    hpsBasicData,
    correlations,
    hpsBasicData.staticBottomholePressurePSIA,
    hpsBasicData.bottomholeTemperatureF,
    primaryIntakeInnerDiameterInches,
    desiredFlowRates,
    hpsFluidPropertiesAtIntakeContext.flowRatesInSitu,
    hpsFluidPropertiesAtIntakeContext.flowRatePrerequisites,
    customFluidProperties,
    customViscosityProperties,
    bubblePointPressurePSIA,
    dewPointPressurePSIA,
    0,
    0,
    fluidPropertiesOverrides,
    condensateApi,
    true
  );

  const hpsFluidPropertiesAfterAllGasSeparationAtPumpContext =
    getFluidPropertiesContext(
      hpsBasicData,
      correlations,
      hpsBasicData.staticBottomholePressurePSIA,
      hpsBasicData.bottomholeTemperatureF,
      primaryIntakeInnerDiameterInches,
      desiredFlowRates,
      hpsFluidPropertiesAtPumpIntakeContext.flowRatesInSitu,
      hpsFluidPropertiesAtPumpIntakeContext.flowRatePrerequisites,
      customFluidProperties,
      customViscosityProperties,
      bubblePointPressurePSIA,
      dewPointPressurePSIA,
      0,
      0,
      fluidPropertiesOverrides,
      condensateApi,
      true
    );

  const hpsFluidPropertiesAfterPumpDischargeContext = getFluidPropertiesContext(
    hpsBasicData,
    correlations,
    requiredDischargePressurePSIA,
    hpsBasicData.bottomholeTemperatureF,
    primaryPumpInnerDiameterInches,
    desiredFlowRates,
    hpsFluidPropertiesAtPumpIntakeContext.flowRatesInSitu,
    hpsFluidPropertiesAtPumpIntakeContext.flowRatePrerequisites,
    customFluidProperties,
    customViscosityProperties,
    bubblePointPressurePSIA,
    dewPointPressurePSIA,
    0,
    0,
    fluidPropertiesOverrides,
    condensateApi,
    true
  );

  return {
    fluidPropertiesCalculationContexts: {
      hpsFluidPropertiesAtIntakeContext,
      hpsFluidPropertiesAtPumpIntakeContext,
      hpsFluidPropertiesAfterAllGasSeparationAtPumpContext,
      hpsFluidPropertiesAfterPumpDischargeContext
    }
  };
};
